/**
 * /chat 답변 속 완성형 HTML 코드블록을 우측 패널에서 실행해 보여 주기 위한 순수 함수 모음.
 *
 * 보안 전제: 결과 문서는 반드시 `sandbox`에 allow-same-origin 이 없는 iframe(srcdoc)으로만 띄운다.
 * 그래야 AI가 만든 스크립트가 왓퀴즈 origin(로그인 토큰이 든 IndexedDB·localStorage)에 닿지 못한다.
 */

export const HTML_PREVIEW_MESSAGE_SOURCE = 'whatquiz-html-preview';

// 실제 사용자 HTML 144개에서 쓰인 기능 기준(2026-10 분석): alert/confirm 17%, 내려받기 16%, 폼 13%, 새 탭 링크 9%.
export const HTML_PREVIEW_SANDBOX = [
  'allow-scripts',
  'allow-modals',
  'allow-forms',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  'allow-downloads',
  'allow-pointer-lock',
].join(' ');

export const HTML_PREVIEW_ALLOW = 'clipboard-write; fullscreen; autoplay';

const SCRIPT_CDN_ALLOWLIST = [
  'https://cdn.jsdelivr.net',
  'https://cdnjs.cloudflare.com',
  'https://unpkg.com',
  'https://cdn.tailwindcss.com',
  'https://cdn.sheetjs.com',
  'https://code.jquery.com',
  'https://esm.sh',
  'https://whatquiz.co.kr/figure/',
];

// /chat 수학·과학 그림 HTML 이 불러오는 왓퀴즈 도움 파일(frontend/public/figure). 내려받은 HTML 은 운영 주소를 그대로 쓴다.
export const FIGURE_LIB_ORIGIN = 'https://whatquiz.co.kr';
const FIGURE_LIB_URL_RE = /https:\/\/whatquiz\.co\.kr(\/figure\/wq-(?:figure|visual)-v\d+\.js)/g;

const buildPreviewCsp = (extraScriptSources = []) => [
  `script-src 'unsafe-inline' 'unsafe-eval' blob: ${[...SCRIPT_CDN_ALLOWLIST, ...extraScriptSources].join(' ')}`,
  'worker-src blob:',
  "object-src 'none'",
  "base-uri 'none'",
  "form-action 'none'",
].join('; ');

// 외부 스크립트는 알려진 CDN에서만, 폼 전송으로 패널이 다른 사이트로 바뀌는 것은 막는다.
export const HTML_PREVIEW_CSP = buildPreviewCsp();

const currentOrigin = () => {
  try {
    return typeof window !== 'undefined' ? window.location.origin : '';
  } catch (error) {
    return '';
  }
};

/** 미리보기에서만 도움 파일을 지금 사이트에서 불러온다(로컬·테스트 서버는 운영 배포 전이라도 그림이 보이게). */
export const rewriteFigureLibForOrigin = (html, origin = currentOrigin()) => {
  const source = String(html || '');
  if (!/^https?:\/\/[^/\s"'<>]+$/.test(origin || '') || origin === FIGURE_LIB_ORIGIN) {
    return { html: source, scriptSources: [] };
  }
  FIGURE_LIB_URL_RE.lastIndex = 0;
  if (!FIGURE_LIB_URL_RE.test(source)) return { html: source, scriptSources: [] };
  FIGURE_LIB_URL_RE.lastIndex = 0;
  return {
    html: source.replace(FIGURE_LIB_URL_RE, (_match, path) => `${origin}${path}`),
    scriptSources: [`${origin}/figure/`],
  };
};

const PREVIEWABLE_LANGUAGES = new Set(['html', 'htm', 'xhtml']);
const UNLABELED_LANGUAGES = new Set(['', 'code', 'text', 'plaintext']);
const FULL_DOCUMENT_RE = /<!doctype\s+html|<html[\s>]|<body[\s>]/i;
const DOCUMENT_START_RE = /^\s*(<!--[\s\S]*?-->\s*)*(<!doctype\s+html|<html[\s>])/i;

export const normalizeLanguage = (language) => (
  String(language || '').replace(/^language-/, '').trim().toLowerCase()
);

/** 코드블록이 그대로 실행 가능한 "완성형 웹페이지"인지 (짧은 예시 조각은 제외). */
export const isPreviewableHtml = (language, code) => {
  const lang = normalizeLanguage(language);
  const text = String(code || '');
  if (PREVIEWABLE_LANGUAGES.has(lang)) return FULL_DOCUMENT_RE.test(text);
  if (UNLABELED_LANGUAGES.has(lang)) return DOCUMENT_START_RE.test(text);
  return false;
};

const OPEN_FENCE_RE = /^ {0,3}(`{3,}|~{3,})[ \t]*([^\s`]*)[^`\n]*$/;
const CLOSE_FENCE_RE = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;

/**
 * 마크다운에서 펜스 코드블록을 순서대로 꺼낸다. 스트리밍 중 닫히지 않은 마지막 블록은 complete=false.
 * react-markdown 과 같은 규칙(같은 문자·같거나 긴 길이로 닫음)을 따른다.
 */
export const extractFencedBlocks = (content) => {
  const lines = String(content || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let current = null;
  lines.forEach((line) => {
    if (!current) {
      const open = line.match(OPEN_FENCE_RE);
      if (open) {
        const indent = line.length - line.trimStart().length;
        current = { fence: open[1], language: normalizeLanguage(open[2]), indent, lines: [] };
      }
      return;
    }
    const close = line.match(CLOSE_FENCE_RE);
    if (close && close[1][0] === current.fence[0] && close[1].length >= current.fence.length) {
      blocks.push({ language: current.language, code: current.lines.join('\n'), complete: true });
      current = null;
      return;
    }
    const stripped = current.indent > 0
      ? line.replace(new RegExp(`^ {0,${current.indent}}`), '')
      : line;
    current.lines.push(stripped);
  });
  if (current) {
    blocks.push({ language: current.language, code: current.lines.join('\n'), complete: false });
  }
  return blocks;
};

export const extractPreviewableHtmlBlocks = (content) => (
  extractFencedBlocks(content).filter((block) => isPreviewableHtml(block.language, block.code))
);

/** 코드블록 텍스트와 마크다운 원문 블록을 같은 것으로 맞춰 보기 위한 정규화(들여쓰기·줄끝 차이 무시). */
export const normalizeHtmlForMatch = (code) => String(code || '')
  .replace(/\r\n?/g, '\n')
  .split('\n')
  .map((line) => line.trim())
  .join('\n')
  .trim();

export const countLines = (code) => {
  const text = String(code || '');
  if (!text) return 0;
  return text.split('\n').length;
};

/** <title> 이나 첫 제목에서 사람이 읽을 이름을 뽑는다. 없으면 기본값. */
export const extractHtmlTitle = (code, fallback = '웹페이지') => {
  const text = String(code || '');
  const pick = (re) => {
    const m = text.match(re);
    if (!m) return '';
    return m[1].replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };
  const title = pick(/<title[^>]*>([\s\S]*?)<\/title>/i) || pick(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (!title) return fallback;
  return title.length > 40 ? `${title.slice(0, 40)}…` : title;
};

/** 파일 이름으로 쓸 수 없는 글자를 지운 `.html` 이름. */
export const buildHtmlFilename = (code) => {
  const base = extractHtmlTitle(code, '왓퀴즈_웹페이지')
    .replace(/…$/, '')
    .split('')
    .filter((ch) => ch.charCodeAt(0) >= 32)
    .join('')
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 60)
    || '왓퀴즈_웹페이지';
  return `${base}.html`;
};

/**
 * 미리보기에서 그대로는 안 되는 경우를 미리 알려 준다(하얀 화면만 보고 당황하지 않도록).
 * 실제 사용자 HTML에서 관찰된 경우만 다룬다.
 */
export const detectHtmlPreviewLimits = (code) => {
  const text = String(code || '');
  const limits = [];
  if (/google\.script\.run/.test(text)) limits.push('apps_script');
  if (/\bchrome\.(?:runtime|storage|tabs|scripting|action)\b/.test(text)) limits.push('chrome_extension');
  if (/\bindexedDB\b/.test(text)) limits.push('indexed_db');
  const relative = text.match(/<(?:script|link)[^>]+(?:src|href)\s*=\s*["'](?!https?:|\/\/|data:|blob:|#)([^"']+\.(?:js|jsx|ts|tsx|css|mjs))["']/i);
  if (relative) limits.push('local_files');
  return limits;
};

const NON_WEB_APP_CONTEXT_RE = /Code\.gs|google\.script\.run|앱스\s*스크립트|앱스크립트|Apps\s*Script|manifest\.json|크롬\s*확장|chrome\.(?:runtime|storage|tabs)\b/i;

/**
 * 앱스 스크립트·크롬 확장 답변 속 HTML(Index.html·popup.html)은 여기서 제대로 돌지 않는다 — 자동으로 열지 않고 버튼으로만 연다.
 * 답변을 쓰는 중에 판단하므로 코드 앞 설명·앞선 코드블록(Code.gs·manifest.json)을 본다.
 */
export const shouldSkipAutoPreview = (content) => NON_WEB_APP_CONTEXT_RE.test(String(content || ''));

export const HTML_PREVIEW_LIMIT_MESSAGES = {
  apps_script: '구글 앱스 스크립트에서 쓰는 화면이라, 여기서는 시트 저장 같은 기능이 동작하지 않아요.',
  chrome_extension: '크롬 확장 프로그램 화면이라, 여기서는 확장 기능은 동작하지 않고 모양만 볼 수 있어요.',
  indexed_db: '브라우저 저장 기능(IndexedDB)을 써서 미리보기에서는 일부가 안 될 수 있어요. 「.html 받기」로 열어 보세요.',
  local_files: '다른 파일(.js·.css)이 함께 있어야 하는 코드라 이 화면만으로는 다 보이지 않을 수 있어요.',
};

/**
 * iframe 안에서 가장 먼저 실행되는 작은 스크립트.
 * - 격리된 iframe에서는 localStorage 접근이 오류를 내 화면 전체가 멈춘다(실사용 HTML의 22%가 사용) → 메모리 저장소로 대신한다.
 * - 오류·콘솔 오류를 부모에 알려 「오류 고치기」에 쓴다.
 * 줄바꿈 없이 한 줄로 넣어 사용자 코드의 오류 줄 번호가 바뀌지 않게 한다.
 */
const buildBridgeScript = (token) => [
  '(function(){',
  `var T=${JSON.stringify(String(token || ''))},S=${JSON.stringify(HTML_PREVIEW_MESSAGE_SOURCE)},n=0;`,
  'function post(t,d){if(n>=60)return;n++;try{window.parent.postMessage({source:S,token:T,type:t,data:d},"*")}catch(e){}}',
  'function cut(v){v=String(v);return v.length>500?v.slice(0,500)+"…":v}',
  'function str(v){try{if(v&&v.stack&&v.message)return v.name+": "+v.message;if(v&&typeof v==="object")return JSON.stringify(v)}catch(e){}return String(v)}',
  'function mem(){var d={};return{getItem:function(k){k=String(k);return Object.prototype.hasOwnProperty.call(d,k)?d[k]:null},setItem:function(k,v){d[String(k)]=String(v)},removeItem:function(k){delete d[String(k)]},clear:function(){d={}},key:function(i){return Object.keys(d)[i]||null},get length(){return Object.keys(d).length}}}',
  '["localStorage","sessionStorage"].forEach(function(k){try{window[k].getItem("__wq")}catch(e){try{Object.defineProperty(window,k,{value:mem(),configurable:true})}catch(x){}}});',
  'try{void document.cookie}catch(e){try{Object.defineProperty(document,"cookie",{get:function(){return ""},set:function(){},configurable:true})}catch(x){}}',
  'window.addEventListener("error",function(e){var t=e&&e.target;if(t&&t!==window&&(t.src||t.href)){post("resource",{message:cut("불러오지 못한 파일: "+(t.src||t.href))});return}post("error",{message:cut(e.message||"알 수 없는 오류"),line:e.lineno||0,column:e.colno||0})},true);',
  'window.addEventListener("unhandledrejection",function(e){post("error",{message:cut("Uncaught (in promise) "+str(e.reason))})});',
  'document.addEventListener("securitypolicyviolation",function(e){post("blocked",{message:cut((e.blockedURI||"")+" ("+e.violatedDirective+")")})});',
  'var ce=console.error;console.error=function(){try{post("console",{message:cut(Array.prototype.map.call(arguments,str).join(" "))})}catch(x){}return ce.apply(console,arguments)};',
  'window.addEventListener("load",function(){post("ready",{})});',
  // 답변 속에 바로 그린 그림은 내용 높이에 맞춰 iframe 높이를 바꾼다. 오류 알림 개수(n)와 따로 센다.
  // scrollHeight 는 iframe 높이보다 작아지지 않아 줄어든 그림을 못 따라간다 — <html> 상자 높이를 잰다.
  'var h=0,hn=0;function size(){var v=Math.ceil(document.documentElement.getBoundingClientRect().height);if(!v||Math.abs(v-h)<4||hn>=30)return;h=v;hn++;try{window.parent.postMessage({source:S,token:T,type:"size",data:{height:v}},"*")}catch(e){}}',
  'window.addEventListener("load",function(){size();if(window.ResizeObserver){new ResizeObserver(size).observe(document.documentElement)}setTimeout(size,800)});',
  '})();',
].join('');

const escapeAttr = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');

/**
 * 사용자 HTML 앞에 CSP 메타와 브리지 스크립트를 넣은 srcdoc 문서를 만든다.
 * <!DOCTYPE> 는 맨 앞에 남겨야 표준 모드가 유지되고, CSP 메타는 <head> 안에 있어야 적용된다.
 */
export const buildPreviewDocument = (html, { token = '', origin } = {}) => {
  const { html: source, scriptSources } = rewriteFigureLibForOrigin(html, origin === undefined ? currentOrigin() : origin);
  const injection = `<meta http-equiv="Content-Security-Policy" content="${escapeAttr(buildPreviewCsp(scriptSources))}"><script>${buildBridgeScript(token)}</script>`;

  const headMatch = source.match(/<head(\s[^>]*)?>/i);
  if (headMatch) {
    const at = headMatch.index + headMatch[0].length;
    return source.slice(0, at) + injection + source.slice(at);
  }
  const htmlMatch = source.match(/<html(\s[^>]*)?>/i);
  if (htmlMatch) {
    const at = htmlMatch.index + htmlMatch[0].length;
    return `${source.slice(0, at)}<head>${injection}</head>${source.slice(at)}`;
  }
  const doctypeMatch = source.match(/<!doctype[^>]*>/i);
  if (doctypeMatch) {
    const at = doctypeMatch.index + doctypeMatch[0].length;
    return source.slice(0, at) + injection + source.slice(at);
  }
  return injection + source;
};

/** iframe 이 보낸 메시지 중 우리 브리지가 보낸 것만 통과시킨다. */
export const parsePreviewMessage = (data, token) => {
  if (!data || typeof data !== 'object') return null;
  if (data.source !== HTML_PREVIEW_MESSAGE_SOURCE) return null;
  if (token && data.token !== token) return null;
  const allowed = ['error', 'console', 'resource', 'blocked', 'ready', 'size'];
  if (!allowed.includes(data.type)) return null;
  if (data.type === 'size') {
    return { type: 'size', height: Number.isFinite(data.data?.height) ? data.data.height : 0 };
  }
  const message = typeof data.data?.message === 'string' ? data.data.message.slice(0, 600) : '';
  return {
    type: data.type,
    message,
    line: Number.isFinite(data.data?.line) ? data.data.line : 0,
    column: Number.isFinite(data.data?.column) ? data.data.column : 0,
  };
};

export const isChatMobileInlineFigureEnabled = () => (
  !['false', '0', 'off'].includes(String(process.env.REACT_APP_CHAT_MOBILE_INLINE_FIGURE || '').trim().toLowerCase())
);

// 답변 안에 바로 그리는 그림은 경고창·전체 화면을 열 수 없다(스크롤하다 갑자기 뜨지 않게). 그림 저장(PNG) 내려받기는 둔다.
export const HTML_INLINE_FIGURE_SANDBOX = [
  'allow-scripts',
  'allow-downloads',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
].join(' ');

const FIGURE_HINT_RE = /wq-(?:figure|visual|map-[a-z]+)-v\d+\.js|jsxgraph|\bchart(?:\.umd)?(?:\.min)?\.js|\/npm\/chart\.js|echarts|plotly|\bd3(?:\.v\d+)?(?:\.min)?\.js|mermaid|<figure[\s>]|<svg[\s>]|<canvas[\s>]/i;
const INTERACTIVE_RE = /<(?:form|input|textarea|select|button|audio|video|iframe)[\s>]|\b(?:alert|confirm|prompt)\s*\(|getUserMedia|requestFullscreen|\bfetch\s*\(|XMLHttpRequest/i;

/**
 * 휴대폰에서 답변 안에 바로 그려도 되는 "그림" HTML 인지 — 수학·과학 그림, 도식, 그래프.
 * 입력칸·버튼·경고창이 있는 웹앱(퀴즈 게임·설문 등)은 지금처럼 「크게 보기」 패널에서만 실행한다.
 */
export const isInlineFigureHtml = (code) => {
  const text = String(code || '');
  return FIGURE_HINT_RE.test(text) && !INTERACTIVE_RE.test(text) && detectHtmlPreviewLimits(text).length === 0;
};

/** 「오류 고치기」로 입력창에 넣을 문장. 사용자 말풍선은 마크다운을 그리지 않으므로 백틱 없이 번호만 붙인다. */
export const buildFixErrorsPrompt = (errors) => {
  const unique = [];
  (errors || []).forEach((err) => {
    const line = err.line ? ` (${err.line}번째 줄)` : '';
    const text = `${err.message}${line}`;
    if (text && !unique.includes(text)) unique.push(text);
  });
  const list = unique.slice(0, 5).map((text, i) => `오류 ${i + 1}: ${text.replace(/`/g, "'")}`).join('\n');
  return `미리보기에서 아래 오류가 났어요. 원인을 찾아 고친 뒤, 생략 없이 전체 HTML 코드를 다시 보여 주세요.\n\n${list}`;
};

/** Blob 을 a[download] 로 저장한다. window.open(blob)은 왓퀴즈 origin 을 물려받아 위험하므로 쓰지 않는다. */
export const downloadHtmlFile = (code, filename) => {
  const blob = new Blob([String(code || '')], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || buildHtmlFilename(code);
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
